import React, { useState } from 'react';
import { apiFetch } from '../api/client';

type Props = { tenantId: string; fileUrl: string; onClose?: () => void };

export function MenuImportReview({ tenantId, fileUrl, onClose }: Props) {
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<any | null>(null);
    const [commitStats, setCommitStats] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);

    const prepare = async () => {
        setLoading(true); setError(null); setCommitStats(null);
        try {
            const formData = new FormData();
            // recupera blob del file remoto (già caricato via upload controller)
            const fetched = await fetch(fileUrl);
            if (!fetched.ok) throw new Error('Download file failed');
            const blob = await fetched.blob();
            formData.append('file', new File([blob], fileUrl.split('/').pop() || 'file.pdf', { type: blob.type }));
            const res = await apiFetch(`/api/menus/${tenantId}/bulk-prepare`, { method: 'POST', body: formData });
            if (!res.ok) throw new Error('Prepare fallita');
            const data = await res.json();
            // clona per editing locale
            setPreview({
                categories: data.categories || [],
                subcategories: data.subcategories || [],
                items: data.items || []
            });
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const commit = async () => {
        if (!preview) return;
        setLoading(true); setError(null);
        try {
            const res = await apiFetch(`/api/menus/${tenantId}/bulk-commit`, {
                method: 'POST',
                body: JSON.stringify(preview),
                headers: { 'Content-Type': 'application/json' }
            });
            if (!res.ok) throw new Error('Commit fallito');
            const stats = await res.json();
            setCommitStats(stats);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const updateItem = (index: number, field: string, value: string) => {
        setPreview((prev: any) => ({
            ...prev,
            items: prev.items.map((it: any, i: number) => i === index ? { ...it, [field]: value } : it)
        }));
    };

    return (
        <div style={{ padding: '1rem', maxWidth: 800 }}>
            <h3>Importazione Menu (PDF/CSV)</h3>
            <p>File: <a href={fileUrl} target="_blank" rel="noreferrer">{fileUrl.split('/').pop()}</a></p>
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '.5rem' }}>
                <button onClick={prepare} disabled={loading} style={{ padding: '.5rem 1rem', background: '#4361ee', color: '#fff', border: 'none', borderRadius: '.5rem' }}>{loading ? '...' : '1. Prepara'}</button>
                <button onClick={commit} disabled={loading || !preview} style={{ padding: '.5rem 1rem', background: preview ? '#2b9348' : '#999', color: '#fff', border: 'none', borderRadius: '.5rem' }}>2. Commit</button>
                {onClose && <button onClick={onClose}>Chiudi</button>}
            </div>
            {error && <div style={{ color: 'red' }}>{error}</div>}
            {preview && (
                <div style={{ background: '#f7f8fc', padding: '1rem', borderRadius: '.7rem', marginTop: '.5rem' }}>
                    <h4>Items (edit prima del commit)</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                        <thead>
                            <tr style={{ background: '#e0e7ff' }}>
                                <th style={{ textAlign: 'left' }}>Categoria</th>
                                <th style={{ textAlign: 'left' }}>Sottocategoria</th>
                                <th style={{ textAlign: 'left' }}>Nome</th>
                                <th style={{ textAlign: 'left' }}>Descrizione</th>
                                <th style={{ textAlign: 'left' }}>Prezzo</th>
                                <th style={{ textAlign: 'left' }}>Allergeni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preview.items.map((it: any, i: number) => (
                                <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                                    <td>{it.category}</td>
                                    <td>{it.subcategory}</td>
                                    <td><input value={it.name} onChange={e => updateItem(i, 'name', e.target.value)} style={{ width: '100%' }} /></td>
                                    <td><input value={it.description || ''} onChange={e => updateItem(i, 'description', e.target.value)} style={{ width: '100%' }} /></td>
                                    <td><input value={it.price || ''} onChange={e => updateItem(i, 'price', e.target.value)} style={{ width: 70 }} /></td>
                                    <td><input value={(Array.isArray(it.allergens) ? it.allergens.join(', ') : (it.allergens || ''))} onChange={e => updateItem(i, 'allergens', e.target.value)} style={{ width: '100%' }} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {commitStats && (
                <div style={{ marginTop: '1rem', background: '#eef7f1', padding: '.75rem', borderRadius: '.5rem' }}>
                    <strong>Risultato:</strong> {commitStats.categoriesCreated} nuove categorie, {commitStats.subcategoriesCreated} nuove sottocategorie, {commitStats.itemsCreated} prodotti creati, {commitStats.itemsUpdated} aggiornati, {commitStats.itemsSkipped} ignorati.
                </div>
            )}
        </div>
    );
}
